/**
 * Spatial primitives that work uniformly across DIGIPIN, Plus Codes, GeoHash.
 *
 *  - distance(codeA, codeB, system) — metres between cell centres (Haversine)
 *  - neighbors(code, system) — 8 surrounding cell codes
 *  - bbox(code, system) — { polygon: 4 corners, center, sides_metres }
 *  - contains(parent, child, system) — does the parent cell fully contain the child cell?
 *  - truncate(code, system, newPrecision) — same point, lower precision
 */

import type { LatLng, GridSystem } from "./index.js";
import { decodeDigipin } from "./digipin.js";
import { decodePluscode } from "./pluscode.js";
import { decodeGeohash } from "./geohash.js";
import { decodeAny, encodeAny } from "./convert.js";

const EARTH_RADIUS_M = 6_371_000;

export type Bbox = {
  readonly minLat: number;
  readonly maxLat: number;
  readonly minLon: number;
  readonly maxLon: number;
};

export type CellInfo = {
  readonly center: LatLng;
  readonly bounds: Bbox;
  /** 4 corners as [lat, lng] pairs, in order: SW, SE, NE, NW. */
  readonly polygon: ReadonlyArray<readonly [number, number]>;
  /** Approximate width × height in metres, at the cell centre's latitude. */
  readonly widthMetres: number;
  readonly heightMetres: number;
};

/**
 * Decode any code into a uniform CellInfo struct.
 */
export function describe(code: string, system: GridSystem): CellInfo {
  let bounds: Bbox;
  let center: LatLng;
  switch (system) {
    case "digipin": {
      const c = decodeDigipin(code);
      bounds = c.bounds;
      center = c.center;
      break;
    }
    case "pluscode": {
      const c = decodePluscode(code);
      bounds = c.bounds;
      center = c.center;
      break;
    }
    case "geohash": {
      const c = decodeGeohash(code);
      bounds = c.bounds;
      center = c.center;
      break;
    }
  }

  const widthMetres = haversine(
    { lat: center.lat, lng: bounds.minLon },
    { lat: center.lat, lng: bounds.maxLon },
  );
  const heightMetres = haversine(
    { lat: bounds.minLat, lng: center.lng },
    { lat: bounds.maxLat, lng: center.lng },
  );

  const polygon: ReadonlyArray<readonly [number, number]> = [
    [bounds.minLat, bounds.minLon],
    [bounds.minLat, bounds.maxLon],
    [bounds.maxLat, bounds.maxLon],
    [bounds.maxLat, bounds.minLon],
  ];

  return { center, bounds, polygon, widthMetres, heightMetres };
}

/**
 * Haversine great-circle distance in metres.
 */
export function haversine(a: LatLng, b: LatLng): number {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.sin(dLng / 2) ** 2 * Math.cos(lat1) * Math.cos(lat2);
  return 2 * EARTH_RADIUS_M * Math.asin(Math.sqrt(h));
}

/**
 * Distance in metres between the centres of two cells (any system, can mix).
 */
export function distance(
  codeA: string,
  systemA: GridSystem,
  codeB: string,
  systemB: GridSystem,
): number {
  const a = decodeAny(codeA, systemA);
  const b = decodeAny(codeB, systemB);
  return haversine(a, b);
}

/**
 * Return the 8 neighbour cell codes (N, NE, E, SE, S, SW, W, NW).
 * Computed by perturbing the centre by one cell width in each direction
 * and re-encoding at the same precision.
 */
export function neighbors(code: string, system: GridSystem): string[] {
  const cell = describe(code, system);
  const latStep = cell.bounds.maxLat - cell.bounds.minLat;
  const lngStep = cell.bounds.maxLon - cell.bounds.minLon;

  // Sample slightly inside each neighbour to avoid landing back in the original cell
  const out: string[] = [];
  const offsets: Array<[number, number]> = [
    [1, 0], [1, 1], [0, 1], [-1, 1],
    [-1, 0], [-1, -1], [0, -1], [1, -1],
  ];
  const precision = inferPrecision(code, system);
  const seen = new Set<string>([code.toUpperCase()]);

  for (const [dLat, dLng] of offsets) {
    const point: LatLng = {
      lat: cell.center.lat + dLat * latStep,
      lng: cell.center.lng + dLng * lngStep,
    };
    try {
      const neighbour = encodeAny(point, system, precision);
      const key = neighbour.toUpperCase();
      if (!seen.has(key)) {
        seen.add(key);
        out.push(neighbour);
      }
    } catch {
      // Out-of-range neighbour (e.g., off the edge of India for DIGIPIN) — skip
    }
  }
  return out;
}

/**
 * Bounding box of a cell, with polygon corners.
 */
export function bbox(code: string, system: GridSystem): CellInfo {
  return describe(code, system);
}

/**
 * Does the `parent` cell fully contain the `child` cell?
 * (Both cells must be in the same system.)
 */
export function contains(
  parent: string,
  child: string,
  system: GridSystem,
): boolean {
  const p = describe(parent, system);
  const c = describe(child, system);
  return (
    p.bounds.minLat <= c.bounds.minLat &&
    p.bounds.maxLat >= c.bounds.maxLat &&
    p.bounds.minLon <= c.bounds.minLon &&
    p.bounds.maxLon >= c.bounds.maxLon
  );
}

/**
 * Re-encode the same point at a coarser precision.
 */
export function truncate(
  code: string,
  system: GridSystem,
  newPrecision: number,
): string {
  const point = decodeAny(code, system);
  return encodeAny(point, system, newPrecision);
}

/**
 * Infer the (precision, length) of a code from its string form.
 */
function inferPrecision(code: string, system: GridSystem): number {
  switch (system) {
    case "digipin":
      return code.replace(/-/g, "").length;
    case "pluscode": {
      // Strip "+" and trailing "0" padding to get effective precision
      const cleaned = code.replace(/[\s+]/g, "").toUpperCase();
      const trimmed = cleaned.replace(/0+$/, "");
      return trimmed.length;
    }
    case "geohash":
      return code.length;
  }
}
